import { useCallback, useEffect, useRef } from 'react';
import type { FC } from 'react';

import { Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Loader } from '@opendoor/atoms/loader';
import { Text } from '@opendoor/atoms/text';
import {
  AppleAuthRedirectButton,
  CheckEmailForm,
  GoogleAuthRedirectButton,
  ReauthForm,
  RedirectAuthGroup,
} from '@opendoor/auth-fe/forms';
import { analytics } from '@opendoor/observability/slim';

import { getEmailDomainForAnalytics } from '../../helpers/emailAnalytics';
import type { IRequestStatus } from './RequestStatus';

export interface AccountMagicLinkGateProps {
  sessionEmail: string | undefined;
  infoIsLoaded: boolean;
  hasGeneratedPassword: boolean | undefined;
  humanStatusRequestStatus: IRequestStatus;
  requestHumanStatus: (email: string) => void;
  requestResetPassword: (email: string) => void;
  onComplete: () => void;
}

export const AccountMagicLinkGate: FC<AccountMagicLinkGateProps> = ({
  sessionEmail,
  infoIsLoaded,
  hasGeneratedPassword,
  humanStatusRequestStatus,
  requestHumanStatus,
  requestResetPassword,
  onComplete,
}) => {
  const humanStatusRequested = useRef(false);
  const initialHasGeneratedPassword = useRef<boolean | undefined>(undefined);
  const gateAnalyticsSent = useRef(false);
  const humanStatusError = humanStatusRequestStatus.error;
  const humanStatusResolved =
    typeof hasGeneratedPassword === 'boolean' || humanStatusError !== undefined;

  const requestSessionHumanStatus = useCallback(() => {
    if (!sessionEmail) {
      return;
    }
    requestHumanStatus(sessionEmail);
  }, [requestHumanStatus, sessionEmail]);

  useEffect(() => {
    if (!infoIsLoaded || !sessionEmail || humanStatusRequested.current) {
      return;
    }
    humanStatusRequested.current = true;
    requestSessionHumanStatus();
  }, [infoIsLoaded, requestSessionHumanStatus, sessionEmail]);

  useEffect(() => {
    if (!humanStatusResolved || initialHasGeneratedPassword.current !== undefined) {
      return;
    }
    initialHasGeneratedPassword.current = hasGeneratedPassword;
    if (!gateAnalyticsSent.current) {
      gateAnalyticsSent.current = true;
      analytics.trackEvent('login', 'magic-link-account-gate-shown', undefined, {
        hasGeneratedPassword,
      });
    }
  }, [hasGeneratedPassword, humanStatusResolved]);

  useEffect(() => {
    if (
      !humanStatusResolved ||
      humanStatusError ||
      initialHasGeneratedPassword.current !== true ||
      hasGeneratedPassword !== false
    ) {
      return;
    }
    onComplete();
  }, [hasGeneratedPassword, humanStatusError, humanStatusResolved, onComplete]);

  useEffect(() => {
    if (!sessionEmail || hasGeneratedPassword !== true || !humanStatusResolved) {
      return;
    }
    const onFocus = () => requestSessionHumanStatus();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [hasGeneratedPassword, humanStatusResolved, requestSessionHumanStatus, sessionEmail]);

  if (!infoIsLoaded) {
    return <Loader size="2xl" />;
  }

  if (!sessionEmail) {
    return (
      <Col className="w-full max-w-120">
        <Text className="text-body-md text-center">
          We couldn’t load your account email. Sign out and sign in again to manage your account.
        </Text>
      </Col>
    );
  }

  if (!humanStatusResolved) {
    return <Loader size="2xl" />;
  }

  if (humanStatusError) {
    return (
      <Col className="w-full max-w-120 items-center gap-6">
        <Text className="text-body-md text-center">
          We couldn’t verify your account status. Please try again.
        </Text>
        <Button
          analyticsName="cosmos-account-magic-link-gate-retry"
          aria-label="Try again"
          variant="primary"
          onClick={requestSessionHumanStatus}
        >
          Try again
        </Button>
      </Col>
    );
  }

  if (hasGeneratedPassword === true) {
    return (
      <CheckEmailForm
        analyticsName="cosmos-account-magic-link-gate"
        email={sessionEmail}
        hasGeneratedPassword
        onResetValidEmail={() => undefined}
        requestResetPassword={requestResetPassword}
      />
    );
  }

  return (
    <ReauthForm
      title="Sign in again to manage your account"
      analyticsPrefix="cosmos-account-reauth"
      resetPasswordHref="/reset-password"
      onSuccess={() => {
        analytics.trackEvent('login', 'magic-link-account-reauth-success', undefined, {
          emailDomain: getEmailDomainForAnalytics(sessionEmail),
        });
        onComplete();
      }}
      renderAdditionalAuth={() => (
        <Col className="gap-6">
          <RedirectAuthGroup>
            <GoogleAuthRedirectButton
              analyticsPrefix="cosmos-account-reauth"
              redirectIntent="reauth"
              size="medium"
              fullWidth
            />
            <AppleAuthRedirectButton
              analyticsPrefix="cosmos-account-reauth"
              redirectIntent="reauth"
              size="medium"
              fullWidth
              variant="secondaryInverse"
            />
          </RedirectAuthGroup>
        </Col>
      )}
    />
  );
};
